'use client';
import React, { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import moment from 'moment';

import { HomeTranslatedBlogArticle } from '@/types/Blog';
import { cn } from '@/lib/utils';
import { AnimatedLink } from '../AnimatedLink';
import { useRouter } from 'next/navigation';
import getMediaFilterUrl from '@/utils/get-media-filter';

const LazyParagraph = React.lazy(() => import('../Paragraph'));

type Props = {
  article: HomeTranslatedBlogArticle;
  lang: string;
  readMoreLabel: string;
};

export default function BlogArticleListItem({
  article,
  lang,
  readMoreLabel,
}: Props) {
  const { push } = useRouter();
  const articleDate = moment(article.publicationDate).utc();
  const translatedArticle = article.blogArticleTranslation.find(
    (translation) => translation.locale === lang,
  );

  const articleLink = useMemo(() => {
    return `${lang === 'en' ? '' : `${lang}`}/blog/${translatedArticle?.slug}`;
  }, [lang, translatedArticle?.slug]);

  const filteredArticleImage = getMediaFilterUrl({
    mediaFilters: article.media.mediaFilters,
    filterName: 'large_1-1_webp',
  });

  const reportNoImg = () => {
    console.log('[ARTICLE LIST] No image path on image');
    return (
      <Image
        src={`${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`}
        alt={
          article.blogArticleTranslation.find(
            (translation) => translation.locale === 'en',
          )?.title ?? ''
        }
        width={100}
        height={100}
        quality={85}
        className="w-full relative inline-block align-middle h-auto max-w-full"
        sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
      />
    );
  };

  return (
    <div className="flex justify-center w-full flex-1 my-auto md:my-auto h-full">
      <div className="flex flex-col group bg-white shadow-none outline-none overflow-hidden relative w-full transition-all hover:shadow-default duration-300 ease-in-out">
        <div>
          <Link href={articleLink}>
            {article.media && article.media.path ? (
              <Image
                src={filteredArticleImage!}
                alt={translatedArticle?.title ?? ''}
                width={100}
                height={100}
                quality={85}
                className="w-full relative inline-block align-middle h-auto max-w-full"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              />
            ) : (
              reportNoImg()
            )}
          </Link>
        </div>
        <div className="flex flex-col p-4 justify-between text-left">
          <div className="flex flex-col gap-2 text-left">
            <div className="text-sm flex flex-col md:flex-row items-start md:items-center gap-[0.3rem] md:gap-2 z-50">
              <div className="text-[#666]">
                {articleDate.utc().format('DD/MM/YYYY')}
              </div>
              <div className="hidden md:block w-6 h-[.0625rem] bg-gray-700" />
              <div className="flex items-center gap-[0.3rem]">
                {article.categories.map(({ blogCategoryTranslation }) => (
                  <Link
                    key={blogCategoryTranslation.title}
                    className=""
                    href={
                      lang == 'en'
                        ? `/blog/categories/${blogCategoryTranslation.slug}`
                        : `/${lang}/blog/categories/${blogCategoryTranslation.slug}`
                    }
                    aria-label={`Navigate to ${blogCategoryTranslation.title}`}>
                    <span className="cursor-pointer text-blue font-semibold">
                      {blogCategoryTranslation.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              href={articleLink}
              className="flex flex-col justify-between text-gray-900">
              <div className="flex items-start">
                <h3 className="flex-1 text-xl mb-2 lg:text-2xl font-title line-clamp-2 desktop:min-h-[64px]">
                  {translatedArticle?.title}
                </h3>
              </div>
              <div className="flex items-start self-stretch">
                <LazyParagraph
                  content={translatedArticle?.description ?? ''}
                  className={cn(
                    'flex-1 text-sm lg:text-base text-inherit flex flex-col items-start [&>p]:mb-4 line-clamp-2',
                  )}
                />
              </div>
            </Link>
          </div>
          <div
            role="button"
            onClick={() => push(articleLink)}
            className={cn(
              'mt-[20px] max-h-[270px] desktop:mt-2 desktop:opacity-0 desktop:max-h-[450px] desktop:transition-all desktop:duration-300 desktop:ease-in-out desktop:group-hover:opacity-100 flex items-center cursor-pointer',
            )}>
            <AnimatedLink
              className="hidden desktop:block w-fit !mt-0"
              href={articleLink}>
              {readMoreLabel}
            </AnimatedLink>
          </div>
        </div>
      </div>
    </div>
  );
}
